<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import moment from 'moment-timezone';
import { DATE_FIELDS } from '@Constants';
import { getDefaultDateFormat } from '@Helpers';
import type { FieldSchema } from '../../../types';
import { FIELDS_COMPONENTS } from '../../../constants';
import { DEFAULT_DATETIME_FORMAT } from '@Helpers';
import { useSessionStore } from '@Stores';

const props = defineProps<{
  field: FieldSchema;
  model: any;
}>();

const { DATE } = FIELDS_COMPONENTS;

const isTextarea = computed(() => props.field?.settings.type === 'textarea');
const fieldValue = computed(() => {
  const { model } = props;

  const targetKeys = props.field?.render?.targetKey;
  if (targetKeys?.length && model[targetKeys[0]]) return getTargetValue(targetKeys, model);

  const isArray = Array.isArray(model);
  if (isArray) return model.length ? model.join(', ') : '-';

  const isObjectValue = model?.value;
  if (isObjectValue) return isObjectValue;

  const isUndefined = !model;
  if (isUndefined) return '-';

  const isDate = props.field.render?.component === DATE || DATE_FIELDS.includes(String(props.field.settings.type));
  if (isDate) return handleDate(model);

  return model;
});

function handleDate(model: any) {
  const isBirthdayField = props.field.settings.type === 'birthday';

  if (isBirthdayField && model?.birthdayModel)
    return getDefaultDateFormat(model.birthdayModel, props.field.settings?.withTime);

  if (model instanceof Date) return getDefaultDateFormat(model, props.field.settings?.withTime);

  if (props.field.settings?.withTime) {
    const { shelterProfile } = storeToRefs(useSessionStore());
    const shelterTimezone = shelterProfile.value?.timezone;

    return moment(model).tz(shelterTimezone).format(DEFAULT_DATETIME_FORMAT);
  }

  return getDefaultDateFormat(model);
}

function getTargetValue(targetKeys: string[], targetSource: any) {
  let targetValue = targetSource;

  targetKeys.forEach((key) => (targetValue = targetValue[key]));

  return targetValue;
}
</script>

<template>
  <p :class="['whitespace-pre-line text-sm', { 'ml-4': isTextarea }]">{{ fieldValue }}</p>
</template>
